import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ListChecks, Plus, X, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { jobsAPI, JOB_TYPE_LABELS } from '../lib/jobsAPI';

export default function JobTemplates() {
  const [jobType, setJobType] = useState('bas_preparation');
  const [labels, setLabels] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [defaultLabels, setDefaultLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplate(jobType);
    // eslint-disable-next-line
  }, [jobType]);

  const loadTemplate = async (jt) => {
    setLoading(true);
    try {
      const res = await jobsAPI.checklistTemplate(jt);
      setLabels(res.data.labels || []);
      setIsCustom(res.data.is_custom || false);
      setDefaultLabels(res.data.default_labels || []);
    } catch (error) {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const addLabel = () => {
    if (!newLabel.trim()) return;
    setLabels((l) => [...l, newLabel.trim()]);
    setNewLabel('');
  };

  const removeLabel = (idx) => setLabels((l) => l.filter((_, i) => i !== idx));

  const moveLabel = (idx, dir) => {
    setLabels((l) => {
      const next = [...l];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await jobsAPI.saveChecklistTemplate(jobType, labels);
      toast.success('Template saved');
      setIsCustom(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save (admin only)');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await jobsAPI.resetChecklistTemplate(jobType);
      setLabels(res.data.labels || []);
      setIsCustom(false);
      toast.success('Reset to default');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset (admin only)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="job-templates-title">
          Workflow Templates
        </h1>
        <p className="text-muted-foreground mt-1">Edit the default checklist applied when a job of each type is created</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-foreground/90">Job type:</label>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger className="w-72" data-testid="template-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(JOB_TYPE_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isCustom ? (
              <Badge className="bg-violet-100 text-violet-700">Custom</Badge>
            ) : (
              <Badge variant="outline">Default</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <ListChecks className="w-5 h-5 text-primary" />
            Checklist for {JOB_TYPE_LABELS[jobType]}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading...</p>
          ) : (
            <>
              {labels.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No items — add some below.</p>
              ) : (
                labels.map((label, idx) => (
                  <div key={idx} className="flex items-center gap-2 group" data-testid={`template-item-${idx}`}>
                    <span className="text-xs text-muted-foreground/70 w-5">{idx + 1}.</span>
                    <span className="flex-1 text-sm text-foreground/90">{label}</span>
                    <button onClick={() => moveLabel(idx, -1)} className="text-muted-foreground/50 hover:text-muted-foreground text-xs px-1" title="Move up">↑</button>
                    <button onClick={() => moveLabel(idx, 1)} className="text-muted-foreground/50 hover:text-muted-foreground text-xs px-1" title="Move down">↓</button>
                    <button onClick={() => removeLabel(idx)} className="text-muted-foreground/70 hover:text-red-500" title="Remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}

              <div className="flex gap-2 pt-2">
                <Input
                  placeholder="Add checklist item..."
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
                  data-testid="new-template-item"
                />
                <Button onClick={addLabel} className="bg-primary hover:bg-primary/90" size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" size="sm" onClick={handleReset} disabled={saving || !isCustom}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to default
                </Button>
                <Button className="bg-primary hover:bg-primary/90" size="sm" onClick={handleSave} disabled={saving} data-testid="save-template">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
